import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CalendarDays, Clock, ChevronLeft, ChevronRight, CheckCircle2, Briefcase, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { PersonAvatar } from "@/components/common";
import { API, errMsg } from "@/lib/api";

const monthLabel = (d) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
const ymd = (d) => d.toISOString().slice(0, 10);
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

export default function PublicBooking() {
  const { slug } = useParams();
  const [link, setLink] = useState(null);
  const [days, setDays] = useState([]);
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [form, setForm] = useState({ visitor_name: "", visitor_email: "", visitor_message: "" });
  const [confirmed, setConfirmed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cfg }, { data: dys }] = await Promise.all([
          axios.get(`${API}/public/availability/${slug}`),
          axios.get(`${API}/public/availability/${slug}/days`),
        ]);
        setLink(cfg);
        setDays(dys);
      } catch (e) { setError(errMsg(e)); }
    })();
  }, [slug]);

  const openDay = useCallback(async (date) => {
    setSelectedDate(date);
    setSlots(null); setChosen(null);
    try {
      const { data } = await axios.get(`${API}/public/availability/${slug}/slots`, { params: { date } });
      setSlots(data);
    } catch (e) { toast.error(errMsg(e)); setSlots([]); }
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!chosen) return toast.error("Pick a time first");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/availability/${slug}/book`, { start: chosen.start, ...form });
      setConfirmed({ ...data, link, chosen });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const calendarGrid = useMemo(() => {
    const first = startOfMonth(month);
    const firstDow = (first.getDay() + 6) % 7; // Mon-start
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    const openDates = new Set(days.map((d) => d.date));
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return { cells, openDates, today };
  }, [month, days]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center hero-gradient px-4">
      <div className="bg-card border border-border rounded-2xl p-10 max-w-md text-center">
        <p className="font-display font-semibold text-xl">Link unavailable</p>
        <p className="text-sm text-muted-foreground mt-2">{error}</p>
      </div>
    </div>
  );
  if (!link) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
  );

  if (confirmed) return (
    <div className="min-h-screen flex items-center justify-center hero-gradient px-4" data-testid="booking-confirmed">
      <div className="bg-card border border-border rounded-2xl p-8 sm:p-10 max-w-md w-full text-center">
        <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
        <h1 className="font-display text-2xl font-semibold mt-4">You're on the calendar</h1>
        <p className="text-sm text-muted-foreground mt-2">
          {new Date(confirmed.start).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })} UTC
        </p>
        <p className="text-sm mt-4">A confirmation email is on its way to <strong>{form.visitor_email}</strong>.</p>
        <Link to="/" className="inline-flex items-center gap-1.5 mt-6 text-sm text-primary font-medium">
          <Briefcase className="h-4 w-4" /> Virtual CEO
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen hero-gradient" data-testid="booking-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm grid md:grid-cols-[320px_1fr]">
          <aside className="p-6 sm:p-8 border-b md:border-b-0 md:border-r border-border bg-muted/30">
            <PersonAvatar name={link.owner.name} src={link.owner.picture} className="h-14 w-14 text-lg" />
            <p className="text-xs text-muted-foreground uppercase tracking-wider mt-5">{link.company.name}</p>
            <h1 className="font-display font-semibold text-2xl mt-1" data-testid="booking-link-name">{link.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">with {link.owner.name}</p>
            {link.description && <p className="text-sm mt-5 whitespace-pre-wrap">{link.description}</p>}
            <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><Clock className="h-4 w-4" /> {link.duration_minutes} minutes</li>
              <li className="flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Next {link.horizon_days} days</li>
            </ul>
          </aside>
          <section className="p-6 sm:p-8">
            {!chosen ? (<>
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setMonth((m) => addMonths(m, -1))} className="p-1.5 rounded-md hover:bg-muted" aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></button>
                <p className="font-medium" data-testid="booking-month-label">{monthLabel(month)}</p>
                <button onClick={() => setMonth((m) => addMonths(m, 1))} className="p-1.5 rounded-md hover:bg-muted" aria-label="Next month"><ChevronRight className="h-4 w-4" /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="text-[11px] font-medium text-muted-foreground py-1">{d}</div>)}
                {calendarGrid.cells.map((cell, i) => {
                  if (!cell) return <div key={i} />;
                  const key = ymd(cell);
                  const isOpen = calendarGrid.openDates.has(key);
                  const isPast = cell < calendarGrid.today;
                  const isSelected = selectedDate === key;
                  return (
                    <button key={i} onClick={() => isOpen && openDay(key)} disabled={!isOpen || isPast} data-testid={isOpen ? "booking-open-day" : undefined}
                      className={`aspect-square rounded-lg text-sm font-medium transition-colors ${
                        isSelected ? "bg-primary text-primary-foreground" :
                        isOpen ? "bg-emerald-50 text-emerald-900 hover:bg-emerald-100 border border-emerald-200" :
                        isPast ? "text-muted-foreground/40" : "text-muted-foreground"}`}>
                      {cell.getDate()}
                    </button>
                  );
                })}
              </div>
              {selectedDate && (
                <div className="mt-6">
                  <p className="text-sm font-medium mb-2">Available times on {new Date(selectedDate).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
                  {slots === null ? <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading&hellip;</div>
                    : slots.length === 0 ? <p className="text-sm text-muted-foreground">No times available on this day.</p>
                    : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-slots-grid">
                        {slots.map((s) => (
                          <button key={s.start} onClick={() => setChosen(s)}
                            className="text-sm font-medium bg-card border border-border rounded-lg py-2 hover:border-primary hover:text-primary transition-colors"
                            data-testid="booking-slot-button">
                            {s.label}
                          </button>
                        ))}
                      </div>
                    )
                  }
                </div>
              )}
            </>) : (
              <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
                <button type="button" onClick={() => setChosen(null)} className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground" data-testid="booking-change-time">
                  <ArrowLeft className="h-3.5 w-3.5" /> Change time
                </button>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-emerald-900">
                    {new Date(chosen.start).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} &middot; {chosen.label}
                  </p>
                  <p className="text-xs text-emerald-800/80">{link.duration_minutes} minutes</p>
                </div>
                <div className="space-y-1.5"><Label>Your name</Label>
                  <Input required value={form.visitor_name} onChange={(e) => setForm({ ...form, visitor_name: e.target.value })} data-testid="booking-visitor-name" />
                </div>
                <div className="space-y-1.5"><Label>Email</Label>
                  <Input required type="email" value={form.visitor_email} onChange={(e) => setForm({ ...form, visitor_email: e.target.value })} data-testid="booking-visitor-email" />
                </div>
                <div className="space-y-1.5"><Label>Anything you'd like {link.owner.name} to know? (optional)</Label>
                  <Textarea rows={3} value={form.visitor_message} onChange={(e) => setForm({ ...form, visitor_message: e.target.value })} data-testid="booking-visitor-message" />
                </div>
                <Button type="submit" size="lg" className="w-full" disabled={busy} data-testid="booking-submit">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Confirm booking
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">By booking you agree to receive a confirmation email at the address above.</p>
              </form>
            )}
          </section>
        </div>
        <p className="text-xs text-center text-muted-foreground mt-5">Powered by Virtual CEO</p>
      </div>
    </div>
  );
}
